import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { VideoThumbnail } from "../../types/cms";
import { firstLogo } from "../../lib/catalog";
import { fallbackThumbnail } from "../../lib/videoSources";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Grid card. Videos with a hosted file play a muted preview on hover.
 * Click opens the quick-view popup; middle-click / cmd-click opens the page.
 */
export default function VideoCard({
  video,
  onOpen,
  isAdmin = false,
  showBrand = true,
}: {
  video: VideoThumbnail;
  onOpen?: (video: VideoThumbnail) => void;
  isAdmin?: boolean;
  showBrand?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const previewRef = useRef<HTMLVideoElement>(null);
  const thumb = video.thumbnailURL || fallbackThumbnail(video.videoURL) || video.styleframesURLs?.[0];
  const canPreview = !!video.videoFileURL && !prefersReducedMotion();

  const start = () => {
    setHovered(true);
    if (canPreview && previewRef.current) {
      previewRef.current.currentTime = Math.min(2, (previewRef.current.duration || 4) / 4);
      previewRef.current.play().catch(() => undefined);
    }
  };
  const stop = () => {
    setHovered(false);
    previewRef.current?.pause();
  };

  return (
    <div className="relative flex flex-col gap-4 w-full group" onMouseEnter={start} onMouseLeave={stop}>
      <Link
        to={`/video/${video.id}`}
        onClick={(e) => {
          if (!onOpen || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onOpen(video);
        }}
        className="relative block aspect-video overflow-hidden bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900"
        aria-label={`${video.title} — ${video.videoFor?.name ?? ""}`}
      >
        {thumb && (
          <img src={thumb} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {canPreview && (
          <video
            ref={previewRef}
            src={`${video.videoFileURL}#t=2`}
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              hovered ? "opacity-100" : "opacity-0"
            }`}
          />
        )}
      </Link>
      {isAdmin && hovered && (
        <Link
          to={`/admin/video/${video.id}`}
          className="absolute top-3 right-3 bg-black text-white text-sm px-4 py-2 rounded-full"
        >
          Modifier
        </Link>
      )}
      <div className="flex gap-4 items-start">
        {showBrand && firstLogo(video.videoFor) && (
          <img src={firstLogo(video.videoFor)} alt="" className="w-10 h-10 rounded-lg object-contain" />
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <h2 className="font-bold text-base truncate">{video.title}</h2>
          <p className="text-sm text-neutral-700 truncate">
            {showBrand ? video.videoFor?.name : video.producedBy?.id ? `by ${video.producedBy.name}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}
